import Plyr from 'plyr'
import { RefObject } from 'react'

import VoiceOverService from 'app/services/voiceover/voiceover.service'
import EventService from 'app/services/analitics/event.service'
import { getCurrentPage } from 'app/services/analitics/util'

const SOUND_MODALS = {
  SOUNDS_DEFAULT: 'sounds-default',
  SOUNDS_VIDEO: 'sounds-video',
  SOUNDS_SONG: 'sounds-song',
}

type BOOK_DETAILS = {
  id: number
  title: string
  viewed: number
}

const _prepareSoundsModalEvent = (soundId: number, soundTitle: string) =>
  EventService.getEvents() &&
  EventService.createEvent()
    .addContextExtension('hub-section', getCurrentPage())
    .addContextExtension('sounds-unit', EventService.soundUnit)
    .addContextExtension('sounds-id', soundId)
    .addContextExtension('sounds-title', soundTitle)

const _prepareSoundsPopupDefaultEvent = (soundId: number, soundTitle: string) =>
  _prepareSoundsModalEvent(soundId, soundTitle).addContextExtension(
    'sounds-modal',
    SOUND_MODALS.SOUNDS_DEFAULT
  )

const _prepareSoundModalNavEvent = (
  soundId: number,
  soundTitle: string,
  soundModal: string,
  verbId: string,
  objectId: string
) =>
  EventService.getEvents() &&
  _prepareSoundsModalEvent(soundId, soundTitle)
    .addContextExtension('sounds-modal', soundModal)
    .addVerbId(verbId)
    .addVerb('display', { [EventService.language]: verbId })
    .addObject('id', objectId)
    .addObjectDefinition('objectType', 'Activity')

/**
 * Prepare an xAPI event for ebook reader.
 */
const _prepareEbookEvent = (bookDetails: BOOK_DETAILS) =>
  EventService.getEvents() &&
  EventService.createEvent()
    .addContextExtension('hub-section', getCurrentPage())
    .addContextExtension('library-title', bookDetails.title)
    .addContextExtension('library-id', bookDetails.id)
    .addContextExtension('ereader-progress', bookDetails.viewed)
    .addContextExtension(
      'ereader-page-num',
      new VoiceOverService().getEventPageID()
    )
    .addObjectDefinition('objectType', 'Activity')

/**
 * Prepare an xAPI event for video player.
 */
const _createVideoPlayerEvent = (plyrRef?: RefObject<Plyr | null>) => {
  if (plyrRef) {
    return EventService.createEvent()
      .addContextExtension('video-speed', plyrRef.current?.speed)
      .addContextExtension('video-quality', plyrRef.current?.quality)
      .addContextExtension('video-timestamp', plyrRef.current?.currentTime)
  }
  return EventService.createEvent()
}

/**
 * Send an xAPI event when user clicks on play/pause button in onboarding video.
 */
const sendOnbordingVideoPlayerEvent = (
  action: string,
  plyrRef: RefObject<Plyr | null>
) =>
  EventService.getEvents() &&
  _createVideoPlayerEvent(plyrRef)
    .addContextExtension('hub-section', getCurrentPage())
    .addVerbId(action)
    .addVerb('display', { [EventService.language]: action })
    .addObject('id', 'onboarding-video')
    .addObjectDefinition('objectType', 'Activity')
    .emit()

/**
 * Send an xAPI event when user clicks on play/pause button in knowledge builder video.
 */
const sendKBVideoPlayerEvent = (
  action: string,
  kbId: string,
  kbTitle: string,
  plyrRef?: RefObject<Plyr | null>
) =>
  EventService.getEvents() &&
  _createVideoPlayerEvent(plyrRef)
    .addContextExtension('hub-section', getCurrentPage())
    .addContextExtension('knowledge-builder-id', kbId)
    .addContextExtension('knowledge-builder-title', kbTitle)
    .addVerbId(action)
    .addVerb('display', { [EventService.language]: action })
    .addObject('id', 'theater-video')
    .addObjectDefinition('objectType', 'Activity')
    .emit()

const sendVoiceOverEvent = (
  action: string,
  bookDetails: BOOK_DETAILS,
  object = 'library-book-voiceover'
) =>
  EventService.getEvents() &&
  _prepareEbookEvent(bookDetails)
    .addObject('id', object)
    .addVerbId(action)
    .addVerb('display', { [EventService.language]: action })
    .emit()

const sendTocItemSelectedEvent = (
  bookDetails: BOOK_DETAILS,
  nextChapter: string
) =>
  EventService.getEvents() &&
  _prepareEbookEvent(bookDetails)
    .addContextExtension('ereader-toc-selection', nextChapter)
    .addObject('id', 'library-book-toc-chapter')
    .addVerbId('selected')
    .addVerb('display', { [EventService.language]: 'selected' })
    .emit()

const sendChangePageEvent = (bookDetails: BOOK_DETAILS, object: string) =>
  EventService.getEvents() &&
  _prepareEbookEvent(bookDetails)
    .addObject('id', object)
    .addVerbId('opened')
    .addVerb('display', { [EventService.language]: 'opened' })
    .emit()

const sendZoomEvent = (bookDetails: BOOK_DETAILS, object: string) =>
  EventService.getEvents() &&
  _prepareEbookEvent(bookDetails)
    .addObject('id', object)
    .addVerbId('selected')
    .addVerb('display', { [EventService.language]: 'selected' })
    .emit()

const sendPlaySoundEvent = (soundId: number, soundTitle: string) =>
  EventService.getEvents() &&
  _prepareSoundsPopupDefaultEvent(soundId, soundTitle)
    .addObject('id', 'sounds-audio')
    .addVerbId('played')
    .addVerb('display', { [EventService.language]: 'played' })
    .emit()

const sendSoundModalOpenedEvent = (
  soundId: number,
  soundTitle: string,
  object: string
) =>
  EventService.getEvents() &&
  _prepareSoundsPopupDefaultEvent(soundId, soundTitle)
    .addObject('id', object)
    .addVerbId('opened')
    .addVerb('display', { [EventService.language]: 'opened' })
    .emit()

const sendPDFSelectedEvent = (bookDetails: BOOK_DETAILS) =>
  _prepareEbookEvent(bookDetails)
    .addObject('id', 'library-book-pdf')
    .addVerbId('selected')
    .addVerb('display', { [EventService.language]: 'selected' })
    .emit()

const sendSoundModalVideoEvent = (
  action: string,
  soundId: number,
  soundTitle: string,
  isSong: boolean,
  plyrRef: RefObject<Plyr | null>
) =>
  EventService.getEvents() &&
  _createVideoPlayerEvent(plyrRef)
    .addContextExtension('hub-section', getCurrentPage())
    .addContextExtension('sounds-unit', EventService.soundUnit)
    .addContextExtension('sounds-id', soundId)
    .addContextExtension('sounds-title', soundTitle)
    .addContextExtension(
      'sounds-modal',
      isSong ? SOUND_MODALS.SOUNDS_SONG : SOUND_MODALS.SOUNDS_VIDEO
    )
    .addVerbId(action)
    .addVerb('display', { [EventService.language]: action })
    .addObject('id', isSong ? 'sounds-song' : 'sounds-video')
    .addObjectDefinition('objectType', 'Activity')
    .emit()

const sendSoundModalGoBackEvent = (
  soundId: number,
  soundTitle: string,
  isSong: boolean
) =>
  EventService.getEvents() &&
  _prepareSoundModalNavEvent(
    soundId,
    soundTitle,
    isSong ? SOUND_MODALS.SOUNDS_SONG : SOUND_MODALS.SOUNDS_VIDEO,
    'exited',
    'sounds-video-song'
  ).emit()

const sendSoundModalCloseEvent = (
  soundId: number,
  soundTitle: string,
  isSong: boolean,
  isVideo: boolean
) => {
  let soundModalType = SOUND_MODALS.SOUNDS_DEFAULT
  if (isSong) soundModalType = SOUND_MODALS.SOUNDS_SONG
  if (isVideo) soundModalType = SOUND_MODALS.SOUNDS_VIDEO
  return (
    EventService.getEvents() &&
    _prepareSoundModalNavEvent(
      soundId,
      soundTitle,
      soundModalType,
      'closed',
      'sounds-modal'
    ).emit()
  )
}

const MultimediaEventService = {
  SOUND_MODALS,
  sendOnbordingVideoPlayerEvent,
  sendVoiceOverEvent,
  sendKBVideoPlayerEvent,
  sendTocItemSelectedEvent,
  sendPDFSelectedEvent,
  sendChangePageEvent,
  sendPlaySoundEvent,
  sendSoundModalOpenedEvent,
  sendSoundModalVideoEvent,
  sendSoundModalGoBackEvent,
  sendSoundModalCloseEvent,
  sendZoomEvent,
}

export default MultimediaEventService
